// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import useTapCoin from '@hooks/mutations/useTapCoin';
import { useIsDevNet } from '@hooks/other/useIsDevnet';
import { useIsSmall } from '@hooks/other/useIsSmall';
import useCoins from '@hooks/queries/cn/useCoin';
import React from 'react';
import { Tooltip, Typography } from '@mui/material';
import { useErrorContext } from '../../contexts/ErrorContext';
import { useModuleParty } from '../../contexts/ModuleContext';
import { useScanProxyApiClient } from '../../contexts/ScanProxyApiContext';
import { appTransferContextWithDisclosures } from '../../utils/appTransferContextWithDisclosures';
import { fmt } from '../../utils/common';

export const CantonBalance: React.FC = () => {
  const isDevNet = useIsDevNet();
  const scanProxyApi = useScanProxyApiClient();

  const party = useModuleParty();
  const errorContext = useErrorContext();
  const coin = useCoins();
  const tapCoin = useTapCoin();
  const isSmall = useIsSmall();
  const decimalPlaces = isSmall ? 2 : 8;

  const tap = React.useCallback(async () => {
    const { appTransferContext, disclosedContracts } = await appTransferContextWithDisclosures(
      scanProxyApi,
      30,
    );
    await tapCoin({
      cid: appTransferContext.amuletRules,
      argument: {
        amount: '1000000.0',
        receiver: party,
        openRound: appTransferContext.openMiningRound,
      },
      meta: {
        disclosedContracts,
      },
    }).catch((error: Error) => errorContext.enqueueError(error));
  }, [tapCoin, party, scanProxyApi, errorContext]);

  const balance = coin.data
    ? coin.data.reduce((acc, c) => acc + parseFloat(c.payload.amount.initialAmount), 0)
    : 0;

  return (
    <Tooltip arrow title={isDevNet ? 'Add more CC' : undefined}>
      <Typography
        onClick={isDevNet ? tap : undefined}
        sx={{ cursor: 'pointer', mr: 1 }}
        variant="body2"
      >
        {fmt(balance, decimalPlaces)} CC
      </Typography>
    </Tooltip>
  );
};
